import { createFileRoute } from "@tanstack/react-router";
import { Inbox, Tag, Users } from "lucide-react";
import { FeaturePage } from "@/components/marketing/FeaturePage";
import { alternateLinkList } from "@/lib/i18n/locale";
import heroImg from "@/assets/contacts-hero.webp";
import captureImg from "@/assets/contacts-capture.webp";
import segmentsImg from "@/assets/contacts-segments.webp";

const IMG: Record<string, string> = { hero: heroImg, capture: captureImg, segments: segmentsImg };

export const Route = createFileRoute("/vi/platform/contacts")({
  head: () => ({
    meta: [
      { title: "CRM Liên hệ thông minh — banana.cards" },
      { name: "description", content: "Lead tự thu thập, hồ sơ làm giàu, gắn thẻ thông minh và pipeline vòng đời — CRM làm việc thay bạn." },
      { property: "og:title", content: "CRM Liên hệ thông minh — banana.cards" },
      { property: "og:description", content: "Lead tự thu thập, hồ sơ làm giàu, gắn thẻ thông minh và pipeline vòng đời — CRM làm việc thay bạn." },
      { property: "og:image", content: IMG['hero'] },
      { property: "og:locale", content: "vi_VN" },
    ],
    links: alternateLinkList("/platform/contacts", "vi"),
  }),
  component: Page,
});

function Page() {
  return (
    <FeaturePage
      eyebrow="CRM Liên hệ"
      title="Một CRM thu lead"
      italic="khi bạn đang ngủ."
      description="Mọi lần chạm, quét và form đều đổ vào CRM tự động. Thẻ thông minh, hồ sơ làm giàu và pipeline vòng đời — không gì lọt qua."
      heroImage={IMG['hero']}
      metrics={[{ v: "0", l: "nhập tay" }, { v: "100%", l: "được thu thập" }, { v: "3x", l: "theo dõi nhanh hơn" }]}
      bullets={[
        { icon: Inbox, title: "Lead tự thu thập", desc: "Mọi chia sẻ, quét, form và sự kiện đổ vào một inbox." },
        { icon: Tag, title: "Gắn thẻ thông minh", desc: "AI đề xuất thẻ theo sự kiện, nguồn, vai trò và độ mới." },
        { icon: Users, title: "Hồ sơ phong phú", desc: "Email, mạng xã hội, công ty, lần chạm cuối — tất cả được làm giàu và khử trùng lặp." }
      ]}
      deepDives={[
        { eyebrow: "Thu thập", title: "Một inbox, mọi", italic: "nguồn lead.", description: "Chạm NFC, quét QR, gửi form, quét danh thiếp giấy và webhook đều vào một inbox đã phân loại.", image: IMG["capture"], reverse: false, points: ["Bộ lọc và thẻ theo nguồn", "Khử trùng lặp khi thu thập", "Quy tắc gán cho từng đội", "Tự lưu trữ sau khi theo dõi"] },
        { eyebrow: "Phân khúc", title: "Lọc, phân khúc,", italic: "kích hoạt.", description: "Xây phân khúc thông minh (ví dụ 'gặp tại SaaStr, không theo dõi trong 14 ngày') và kích hoạt tự động hóa hoặc chiến dịch từ đó.", image: IMG["segments"], reverse: true, points: ["Phân khúc thông minh với số đếm trực tiếp", "View lưu theo đội", "Hành động hàng loạt và xuất", "Đồng bộ tới công cụ chiến dịch ngoài"] }
      ]}
      howItWorks={[
        { title: "Thu thập mọi nơi", desc: "Chạm thẻ, quét, form, nhập." },
        { title: "Banana khử trùng lặp", desc: "Một liên hệ, mọi nguồn." },
        { title: "Gắn thẻ và gán", desc: "Quy tắc thông minh theo workspace." },
        { title: "Theo dõi nhanh", desc: "Nhắc, việc cần làm và bản thảo AI." }
      ]}
      capabilities={[
        "Tự thu thập từ mọi nguồn",
        "Khử trùng lặp & gộp",
        "Trường tùy chỉnh và pipeline",
        "Phân khúc thông minh đã lưu",
        "Nhập / xuất hàng loạt",
        "Phân quyền theo đội",
        "Xuất CSV & API",
        "Webhook khi thay đổi"
      ]}
      faq={[
        { q: "Liên hệ mới vào CRM bằng cách nào?", a: "Banana thu thập liên hệ từ mọi hướng mà bạn không cần gõ: mỗi lần chạm NFC và quét QR tạo một liên hệ, form Lead Exchange trên thẻ hỏi đối phương thông tin, AI quét danh thiếp giấy biến ảnh thẻ giấy thành liên hệ sạch, và bạn có thể nhập hàng loạt từ CSV, Google Contacts, Outlook hoặc CRM lớn nào đó." },
        { q: "Có thể quét danh thiếp giấy thành liên hệ không?", a: "Có. Chụp ảnh thẻ giấy trong app Banana và AI quét trích xuất tên, công ty, chức danh, email, điện thoại, website và địa chỉ thành liên hệ có cấu trúc — sau đó khử trùng lặp với danh sách hiện có. Tuyệt cho triển lãm và hội nghị." },
        { q: "Có thể gắn thẻ, phân khúc và lọc liên hệ không?", a: "Có. Tạo thẻ màu không giới hạn (ví dụ 'Lead nóng', 'Khách hàng', 'Nhà cung cấp', 'Đối tác'), gán nhiều thẻ cho mỗi liên hệ, và lọc danh sách theo tổ hợp thẻ + tìm kiếm tự do. Dùng sắp xếp và bộ lọc thẻ để xây phân khúc đã lưu cho outreach hoặc tự động hóa." },
        { q: "Đội tôi dùng chung một pool hay mỗi người riêng?", a: "Workspace chia sẻ một sổ liên hệ — mọi đồng nghiệp thấy cùng liên hệ và timeline, với phân quyền theo vai trò kiểm soát ai được sửa hoặc xóa. Ghi chú cá nhân có thể giữ riêng cho từng người." },
        { q: "Có nhập từ HubSpot, Salesforce hoặc CRM hiện tại không?", a: "Có — nhập CSV một cú nhấp hoạt động với định dạng xuất của mọi CRM lớn (HubSpot, Salesforce, Pipedrive, Zoho, Monday, Close, Folk). Ánh xạ trường tự động cho trường chuẩn và chỉnh được cho trường tùy chỉnh, với phát hiện trùng lặp theo email và điện thoại." },
        { q: "Có API để đẩy liên hệ từ hệ thống riêng không?", a: "Có — REST API đầy đủ cộng webhook bắn khi liên hệ được tạo/cập nhật, và Banana có tích hợp Zapier native với 6.000+ app nếu bạn muốn nối hình ảnh." }
      ]}
      outro={ { title: "Đừng đánh mất lead", italic: "lần nào nữa.", description: "Một inbox cho mọi kết nối — làm giàu, gắn thẻ và sẵn sàng hành động." } }
    />
  );
}